import { forwardRef, useImperativeHandle } from 'react';

import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { useMyAccount } from 'pages/account/my_account/context';
import { FormattedMessage, useIntl } from 'react-intl';
import { Alert, Button, Form } from 'react-bootstrap';
import { useNotification } from 'app/notifications';
import { Modal } from 'components/modal_manager';
import { User, UsersApi } from 'apis/UsersApi';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

export type EditAccountModalHandle = {
    hide: () => void;
    show: () => void;
    visible: boolean;
}

export const EditAccountModal = forwardRef<EditAccountModalHandle, any>((props, forwardedRef) => {

    const { currentUser, refreshAppBasics } = useApp();

    const [state, setState, resetState] = useSuperState({
        error: null as string,
        loading: false,
        userDetails: {
            EarlyAccessPreference: currentUser.earlyAccessPreference,
            Email: currentUser.email,
            FirstName: currentUser.firstName,
            FullName: currentUser.fullName,
            Id: currentUser.id,
            LastName: currentUser.lastName,
            UserName: currentUser.userName,
            UserProfileID: currentUser.userProfileId
        } as Partial<User>,
        visible: false
    });
    const { error, loading, visible } = state;

    const { permissions } = useMyAccount();
    const { formatMessage } = useIntl();
    const { createToast } = useNotification();

    const clearError = () => setState({ error: null });

    const show = () => setState({
        userDetails: {
            EarlyAccessPreference: currentUser.earlyAccessPreference,
            Email: currentUser.email,
            FirstName: currentUser.firstName,
            FullName: currentUser.fullName,
            Id: currentUser.id,
            LastName: currentUser.lastName,
            UserName: currentUser.userName,
            UserProfileID: currentUser.userProfileId
        },
        visible: true
    });

    const hide = () => resetState();

    const setUserDetails = (details: Partial<User>) => setState({ userDetails: { ...state.userDetails, ...details } });

    const editFullName = (value: string, firstOrLast: 'firstName' | 'lastName') => {

        const { FirstName = '', LastName = '' } = state.userDetails ?? {};

        return firstOrLast === 'firstName'
            ? `${value} ${LastName}`
            : `${FirstName} ${value}`;

    };

    const handleSubmit = async (e: any) => {

        try {

            e.preventDefault();
            const { userDetails } = state;

            setState({ loading: true });

            await UsersApi.Update(userDetails);
            await refreshAppBasics();

            setState({ loading: false, visible: false });

            createToast(formatMessage({ id: 'account_details_updated_successfully.eae3fed5', defaultMessage: 'Account details updated successfully.' }), 'success');

            resetState();

        } catch (e){

            setState({
                error: e?.message || e,
                loading: false
            });

        }

    };

    useImperativeHandle(forwardedRef, () => ({
        hide,
        show,
        visible
    } as EditAccountModalHandle));

    return (
        <Modal onHide={hide} show={visible}>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FormattedMessage defaultMessage="Edit Account" id="edit_account.3a8dbb59" />
                </Modal.Title>
            </Modal.Header>

            <Form onSubmit={handleSubmit}>

                <Modal.Body>
                    <Alert dismissible onClose={clearError} show={!!error} variant={'danger'}>
                        {error}
                    </Alert>

                    <div className="mb-3">
                        <p className="text-strong fw-semibold mb-2">
                            <FormattedMessage defaultMessage="Username/Email" id="usernameemail.9e110d3a" />
                        </p>
                        <p>
                            {currentUser.userName}
                        </p>
                    </div>

                    <div className="mb-3 w-50">
                        <GMetrixInput
                            required
                            onChange={e => setUserDetails({
                                FirstName: e.target.value,
                                FullName: editFullName(e.target.value, 'firstName')
                            })}
                            defaultValue={currentUser.firstName}
                            disabled={loading}
                            label={formatMessage({ id: 'first_name.759a675b', defaultMessage: 'First Name' })}
                            readOnly={!permissions.Edit || !permissions.UserEdit}
                            size="sm"
                        />
                    </div>

                    <div className="mb-3 w-50">
                        <GMetrixInput
                            required
                            onChange={e => setUserDetails({
                                FullName: editFullName(e.target.value, 'lastName'),
                                LastName: e.target.value
                            })}
                            defaultValue={currentUser.lastName}
                            disabled={loading}
                            label={formatMessage({ id: 'last_name.029e7273', defaultMessage: 'Last Name' })}
                            readOnly={!permissions.Edit || !permissions.UserEdit}
                            size="sm"
                        />
                    </div>

                    <div>
                        <Form.Label className="fw-bold">
                            <FormattedMessage defaultMessage="Early Access" id="early_access.86082dfe" />
                        </Form.Label>
                        <div className={'d-flex align-items-center gap-3'}>
                            <Form.Check
                                label={state.userDetails.EarlyAccessPreference === 0
                                    ? formatMessage({ id: 'all_default_to_early_access_features_sho.28e0b508', defaultMessage: 'All - Default to Early Access features, show opt-out option.' })
                                    : formatMessage({ id: 'none_default_to_standard_features_dont_s.1a693924', defaultMessage: "None - Default to Standard features, don't show opt-in option." })}
                                checked={state.userDetails.EarlyAccessPreference === 0}
                                disabled={loading}
                                id="earlyAccessToggle"
                                onChange={e => setUserDetails({ EarlyAccessPreference: e.target.checked ? 0 : 2 })}
                                type="switch"
                            />
                            <Form.Text>
                                {state.userDetails.EarlyAccessPreference === 0 ?
                                    (
                                        'Default to early access features'
                                    )
                                    :
                                    (
                                        'Default to standard features'
                                    )
                                }
                            </Form.Text>
                        </div>
                    </div>

                </Modal.Body>

                <Modal.Footer>
                    <Button className="me-2" disabled={loading} onClick={hide} variant="secondary">
                        <FormattedMessage defaultMessage="Cancel" id="cancel.89a86adc" />
                    </Button>
                    <Button disabled={loading} type="submit" variant="primary">
                        <FormattedMessage defaultMessage="Save" id="save.d44082ae" />
                    </Button>
                </Modal.Footer>

            </Form>

        </Modal>
    );

});

EditAccountModal.displayName = 'EditAccountModal';